import { useEffect, useState } from 'react';
import { AppModal } from 'shared/UI/UIKit/Modal';
import { useAppForm } from 'shared/UI/UIKit/Form';
import { getSubjectsList } from 'services/api/subjects';
import { useCreateTeacherWorkloadMutation, useGetEducationClassListQuery } from 'shared/api';
import {
  TeacherWorkloadForm,
  createDefaultWorkDaysForm,
  formWorkDaysToApi,
} from 'entities/teacher-workload/form';
import { TeacherWorkloadModalFooter } from 'entities/teacher-workload/modal-footer';
import type { Subject } from 'services/api/subjects';
import type { TeacherWorkloadFormValues } from 'entities/teacher-workload/form/types';

type CreateTeacherWorkloadModalProps = {
  visible: boolean;
  hide: () => void;
  onSuccess: () => void;
};

export const CreateTeacherWorkloadModal = ({
  visible,
  hide,
  onSuccess,
}: CreateTeacherWorkloadModalProps) => {
  const { data: educationClasses } = useGetEducationClassListQuery({ size: 1000 });

  const [form] = useAppForm<TeacherWorkloadFormValues>();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [createTeacherWorkload, { isLoading: isSubmitting }] = useCreateTeacherWorkloadMutation();

  useEffect(() => {
    if (visible) {
      setLoading(true);
      getSubjectsList()
        .then((res) => {
          setSubjects(res.content);
        })
        .finally(() => {
          setLoading(false);
        });

      form.setFieldsValue({
        workDays: createDefaultWorkDaysForm(),
      });
    }
  }, [visible]);

  const hideModal = () => {
    form.resetFields();
    setError(null);
    hide();
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const createSubjects = values.subjectRows
          .map((s) => ({
            subjectId: s.subjectId!,
            workloadHours: s.workloadHours!,
            educationalClassIds: s.educationalClassIds!,
          }))
          //TODO: Временно удаляем пустые классы, для проверки удобства пользователей - 28.08.2026
          .filter(
            (subject) =>
              Array.isArray(subject.educationalClassIds) && subject.educationalClassIds.length,
          );

        createTeacherWorkload({
          code: values.code!,
          preferredRoomIds: values?.preferredRoomIds ?? [],
          qualifications: createSubjects,
          workDays: formWorkDaysToApi(values.workDays),
        })
          .unwrap()
          .then(() => {
            hideModal();
            onSuccess();
          });
      })
      .catch((err) => {
        console.log('Form validation failed:', err);
        setError(err);
      });
  };

  const footer = (
    <TeacherWorkloadModalFooter hideModal={hideModal} onSubmit={onSubmit} title="Создать" />
  );

  const content = (
    <TeacherWorkloadForm
      form={form}
      availableSubjects={subjects}
      subjectsLoading={loading}
      error={error}
      setError={setError}
      availableEducationClasses={educationClasses?.content || []}
    />
  );

  return (
    <AppModal
      visible={visible}
      onCancel={hideModal}
      content={content}
      destroyOnClose
      footer={footer}
      loading={isSubmitting}
      title="Создать нагрузку учителя"
      maskClosable={false}
      size="auto"
      wrapStyle={{ width: 'auto', alignItems: 'center' }}
      style={{ width: 'auto' }}
    />
  );
};
